/**
 * Use large deal cards for featuring deals or deals, like Large Deal Rail, manual add gifts with purchase, bundle and save rail and others deals that requires actions to be taken.
 *
 * @module views/components/LargeDealRail
 * @memberof -Common
 */
import './LargeDealRail.scss';

import React, { useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import Text from '@ulta/core/components/Text/Text';
import { hasItems } from '@ulta/core/utils/array/array';

import LargeDealCard from '@ulta/components/LargeDealCard/LargeDealCard';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';

import * as utils from './LargeDealRail';

/**
  * Represents a LargeDealRail component
  *
  * @method
  * @param {LargeDealRailProps} props - React properties passed from composition
  * @returns LargeDealRail
  */
export const LargeDealRail = function( props ){
  const largeDealRailRef = useRef( null );

  const {
    dataCapture,
    deals,

    /* Rail Title */
    title,

    /* Rail meta data */
    carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,
    nextAccessibility,
    nextClickAction,
    previousAccessibility,
    previousClickAction
  } = props;

  /* Setting variables */
  const maxWidthAspectRatio = useMemo( () => utils.getMaxAspectRatio( { deals } ), [deals] );
  const [currentIndex, updateCurrentIndex] = useState( 1 );
  const isLastItem =  currentIndex === deals?.length;
  const isSingleItem = deals?.length === 1;

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  /* Interpolating accesibility strings */
  const newCarouselAccessibility = carouselUtils.handleTokenizedString( carouselAccessibility, [title] );
  const newCarouselCardAccessibility = carouselUtils.handleTokenizedString( carouselCardAccessibility, [title] );
  const newCarouselDetailsAccessibility = carouselUtils.handleTokenizedString( carouselDetailsAccessibility, [title] );

  /* ADA Objects */
  const _railADAmeta = {
    carouselAccessibility: newCarouselAccessibility,
    carouselCardAccessibility: newCarouselCardAccessibility,
    carouselDetailsAccessibility: newCarouselDetailsAccessibility
  };

  const _railMeta = {
    nextAccessibility: nextAccessibility,
    previousAccessibility: previousAccessibility
  };

  if( !hasItems( deals ) ){
    return null;
  }

  return (
    <div className='LargeDealRail'
      ref={ largeDealRailRef }
    >
      <GridContainer>
        { title &&
          <div className={
            classNames( 'LargeDealRail__sectionTitle', {
              'LargeDealRail__sectionTitle--singleItem': isSingleItem
            } )
          }
          >
            <Text
              textStyle='title-6'
              htmlTag='h2'
            >
              { title }
            </Text>
          </div>
        }
        <div className={
          classNames( 'LargeDealRail__container', {
            'LargeDealRail__container--singleItem': isSingleItem,
            'LargeDealRail__container--lastItem': !isSingleItem && isLastItem,
            'LargeDealRail__container--firstItem': !isSingleItem
          } )
        }
        >
          <Rail
            railRef={ largeDealRailRef }
            id={ railUuid }
            items={ deals }
            useFreeMode={ false }
            previousClickAction={ previousClickAction }
            nextClickAction={ nextClickAction }
            spaceBetween={ isSingleItem ? 0 : 16 }
            centeredSlides={ true }
            dataCapture={ dataCapture }
            _railADAmeta={ _railADAmeta }
            _railMeta={ _railMeta }
            slidesPerGroupXL={ 1 }
            sliderPerViewXL={ isSingleItem ? 1 : 1.2 }
            slidesPerGroupLG={ 1 }
            sliderPerViewLG={ isSingleItem ? 1 : 1.2 }
            slidesPerGroupMD={ 1 }
            sliderPerViewMD={ isSingleItem ? 1 : 1.5 }
            slidesPerGroupSM={ 1 }
            sliderPerViewSM={ isSingleItem ? 1 : 1.15 }
          >
            { !isSingleItem && deals?.length > 0 && deals.map( ( product, index ) => (
              <Slide key={ `LargeDealRail${ index + 1 } ` }>
                <div
                  className={
                    classNames( 'LargeDealRail__itemclass', {
                      'LargeDealRail__itemclass--lastItem': !isSingleItem && isLastItem,
                      'LargeDealRail__itemclass--singleItem': isSingleItem,
                      'LargeDealRail__itemclass--firstItem': currentIndex === 1
                    } ) }
                >
                  <LargeDealCard
                    item={ product.length }
                    image={ product.image }
                    description={ product.description }
                    tag={ product.tag }
                    eyebrow={ product.eyebrow }
                    headline={ product.headline }
                    action1={ product.actionGroup?.action1 }
                    action2={ product.actionGroup?.action2 }
                    action1Style={ product.actionGroup?.action1Style }
                    action2Style={ product.actionGroup?.action2Style }
                    maxWidthAspectRatio={ maxWidthAspectRatio }
                  />
                </div>
              </Slide>
            ) )
            }
            { isSingleItem && deals?.map( ( product, index ) => (
              <div
                key={ `LargeDealRail__itemclass-${index}` }
                className={
                  classNames( 'LargeDealRail__itemclass', {
                    'LargeDealRail__itemclass--singleItem': isSingleItem,
                    'LargeDealRail__itemclass--firstItem': currentIndex === 1
                  } ) }
              >
                <LargeDealCard
                  item={ product.length }
                  image={ product.image }
                  description={ product.description }
                  tag={ product.tag }
                  eyebrow={ product.eyebrow }
                  headline={ product.headline }
                  action1={ product.actionGroup?.action1 }
                  action2={ product.actionGroup?.action2 }
                  action1Style={ product.actionGroup?.action1Style }
                  action2Style={ product.actionGroup?.action2Style }
                  maxWidthAspectRatio={ maxWidthAspectRatio }
                />
              </div>
            ) ) }
          </Rail>
        </div>
      </GridContainer>
    </div>
  );
};

/**
 * Provide aspectRatio of the Image which has max height
 */

export const getMaxAspectRatio = ( data ) => {
  const { deals } = data || {};
  if( !deals ){
    return 1;
  }

  let maxAspectRatio = deals[0]?.image?.metaData?.aspectRatio.w;
  for ( let i = 1; i < deals.length; i++ ){
    const aspectRatio = deals[i].image?.metaData?.aspectRatio.w;
    maxAspectRatio = Math.min( maxAspectRatio, aspectRatio );
  }
  return maxAspectRatio;
};

/**
  * Property type definitions
  *
  * @typedef LargeDealRailProps
  * @type {object}
  * @property {string} carouselAccessibility - Sets the value for aria label for carousel
  * @property {string} carouselCardAccessibility - Sets carousel item number of total count string
  * @property {string} carouselDetailsAccessibility - Sets Carousel details screen reader information
  * @property {array} deals - Sets the array for the Large Deal
  * @property {string} nextAccessibility - Sets the aria label for next button.
  * @property {object} nextClickAction - Action for when the next slide button is pressed
  * @property {string} previousAccessibility - Sets the aria label for previous button.
  * @property {object} previousClickAction - Action for when the previous slide button is pressed
  * @property {string} title - Sets the title of the ultamate Rewards deals
  */
export const propTypes = {
  /**
   * Sets the value for aria label for carousel
   */
  carouselAccessibility: PropTypes.string,
  /**
   * Sets carousel item number of total count string
   */
  carouselCardAccessibility:PropTypes.string,
  /**
   * Sets Carousel details screen reader information
   */
  carouselDetailsAccessibility:PropTypes.string,
  /**
   * Sets the array for the Large Deal
   */
  deals: PropTypes.array,
  /**
   * Sets the aria label for next button.
   */
  nextAccessibility: PropTypes.string,
  /**
   * Action for when the next slide button is pressed
   */
  nextClickAction: PropTypes.object,
  /**
   * Sets the aria label for previous button.
   */
  previousAccessibility: PropTypes.string,
  /**
   * Action for when the previous slide button is pressed
   */
  previousClickAction: PropTypes.object,
  /**
   * The object that contains the title.
   */
  title: PropTypes.string
};

LargeDealRail.propTypes = propTypes;

export default LargeDealRail;